"use client";

// ============================================================
// CLUBHUB – Dokumente: Dateien hereingeben
// ============================================================
// EIN Weg fuer alle Eingaenge — Knopf, Kamera, Hineinziehen auf
// die Flaeche oder auf einen Ordner im Baum, neue Version im
// Seitenblatt. Wohin es geht, ist immer der Ordner, auf den man
// zeigt; die Fortschrittsanzeige sagt es noch einmal.
//
// Gleicher Name im Zielordner: gefragt wird, ob die Datei eine
// neue VERSION der vorhandenen ist (der haeufigste Fall — das
// ueberarbeitete Protokoll) oder daneben liegen soll. ClubDesk
// hat in diesem Fall stillschweigend ersetzt. Eine neue Version
// muss dieselbe Endung haben: aus der Satzung als PDF wird keine
// Word-Datei, nur weil jemand die falsche erwischt hat.
//
// Die Warteschlange liegt in einem MODUL-SPEICHER mit EINEM Takt,
// nicht in der Seite: wer waehrend des Hochladens zu den Vorlagen
// wechselt, baut die Ablage neu auf — die Dateien kommen trotzdem
// an, und die Anzeige steht danach wieder da.
//
// MIT SITZUNG geht jede Datei per XMLHttpRequest an
// `/api/dokumente` (echter Fortschritt, hoechstens 50 MB); der Server
// prueft Recht, Ordner, Groesse und Inhalt und legt erst die Datei,
// dann den Datensatz an. Danach holt `router.refresh()` den Stand.
//
// MUSTERBESTAND: Der Fortschritt ist gespielt (rund 6 MB/s), die
// Datei bleibt im Browser. Bilder zeigen sich trotzdem echt — als
// blob:-Adresse.
// ============================================================

import { useCallback, useRef, useSyncExternalStore } from "react";
import { useRouter } from "next/navigation";
import { Check, CircleAlert, X } from "lucide-react";
import { confirmDialog } from "@/components/ui/confirm";
import { toast } from "@/components/ui/toast";
import { cn } from "@/lib/utils";
import { formatDecimal } from "@/lib/number-format";
import { dateiArt, endung, formatBytes, freierName, jetztIso, MAX_BYTES, MAX_TEXT, mimeFuer, pfadText, slug, wirksameSicht } from "@/lib/dokumente/format";
import type { AblageDaten, DocumentView } from "@/lib/dokumente/views";
import { aendernSpeicher, bestandJetzt, festhalten, mitDatei, neueId, type Bestand } from "./ablage-zustand";

export type Posten = {
  id: string;
  name: string;
  groesse: number;
  folderId: string;
  ziel: string;
  fortschritt: number;
  status: "laeuft" | "fertig" | "fehler";
  fehler?: string;
  /** Bei einer neuen Version: welche Datei. */
  dateiId?: string;
};

// ── Warteschlange mit einem Takt ─────────────────────────────
type Auftrag = { beginn: number; dauer: number; fertig: () => void };

let posten: Posten[] = [];
const auftraege = new Map<string, Auftrag>();
let uhr: ReturnType<typeof setInterval> | null = null;
const hoerer = new Set<() => void>();
const KEINE: Posten[] = [];

function melden() {
  for (const h of hoerer) h();
}

function abonnieren(h: () => void) {
  hoerer.add(h);
  return () => {
    hoerer.delete(h);
  };
}

function ticken() {
  const jetzt = Date.now();
  const fertig: Auftrag[] = [];
  posten = posten.map((p) => {
    const a = auftraege.get(p.id);
    if (!a || p.status !== "laeuft") return p;
    const anteil = Math.min(1, (jetzt - a.beginn) / a.dauer);
    if (anteil >= 1) {
      fertig.push(a);
      auftraege.delete(p.id);
      return { ...p, fortschritt: 1, status: "fertig" };
    }
    return { ...p, fortschritt: anteil };
  });
  melden();
  for (const a of fertig) a.fertig();
  if (auftraege.size === 0 && uhr) {
    clearInterval(uhr);
    uhr = null;
  }
}

/** Musterbestand: gespielter Fortschritt. */
function starten(p: Posten, fertig: () => void) {
  auftraege.set(p.id, { beginn: Date.now(), dauer: Math.min(6000, Math.max(800, (p.groesse / 6_000_000) * 1000)), fertig });
  posten = [...posten, p];
  melden();
  if (!uhr) uhr = setInterval(ticken, 150);
}

function setze(id: string, teil: Partial<Posten>) {
  posten = posten.map((p) => (p.id === id ? { ...p, ...teil } : p));
  melden();
}

/** Was der Server nach dem Hochladen meldet. */
type Angekommen = { id: string; version: number; name: string };

/** Mit Sitzung: die Datei wirklich hochladen, mit dem Fortschritt des Browsers. */
function sendenEcht(p: Posten, form: FormData, fertig: (a: Angekommen) => void) {
  posten = [...posten, p];
  melden();
  const xhr = new XMLHttpRequest();
  xhr.open("POST", "/api/dokumente");
  xhr.responseType = "json";
  xhr.upload.onprogress = (e) => {
    // Die letzten Prozent gehoeren dem Server (Pruefen, Ablegen).
    if (e.lengthComputable) setze(p.id, { fortschritt: Math.min(0.97, e.loaded / e.total) });
  };
  xhr.onload = () => {
    const r = xhr.response as (Angekommen & { success?: boolean; error?: string }) | null;
    if (xhr.status >= 200 && xhr.status < 300 && r?.success) {
      setze(p.id, { fortschritt: 1, status: "fertig", name: r.name });
      fertig(r);
    } else {
      setze(p.id, { status: "fehler", fehler: r?.error ?? (xhr.status === 413 ? `Zu groß — höchstens ${MAX_TEXT}.` : "Das Hochladen ging nicht.") });
    }
  };
  xhr.onerror = () => setze(p.id, { status: "fehler", fehler: "Keine Verbindung — bitte noch einmal versuchen." });
  xhr.send(form);
}

function fehlschlag(p: Omit<Posten, "fortschritt" | "status">, fehler: string) {
  posten = [...posten, { ...p, fortschritt: 0, status: "fehler", fehler }];
  melden();
}

function laufend(): Posten[] {
  return posten.filter((p) => p.status === "laeuft");
}

// ── Ablegen, wenn eine Datei oben ist ────────────────────────
function neueVersionAblegen(daten: AblageDaten, dateiId: string, datei: File, vomServer?: number): number | null {
  const jetzt = jetztIso();
  const mimeType = mimeFuer(datei.name, datei.type);
  const bild = dateiArt(mimeType, datei.name) === "bild";
  let nummer: number | null = null;
  aendernSpeicher((b) =>
    mitDatei(b, dateiId, (d) => {
      const version = vomServer ?? d.version + 1;
      nummer = version;
      const storageKey = `${daten.storage.pathPrefix ?? ""}${d.id}/v${version}/${slug(d.name)}`;
      return {
        ...d,
        version,
        size: datei.size,
        mimeType,
        storageKey,
        checksum: null,
        uploadedBy: daten.me,
        updatedAt: jetzt,
        lokaleVorschau: bild ? URL.createObjectURL(datei) : d.lokaleVorschau,
        versions: [{ version, storageKey, size: datei.size, mimeType, uploadedBy: daten.me, createdAt: jetzt, note: null }, ...d.versions],
      };
    }),
  );
  return nummer;
}

function neuAblegen(daten: AblageDaten, datei: File, name: string, folderId: string, vomServer?: string) {
  const id = vomServer ?? neueId("d");
  const jetzt = jetztIso();
  const mimeType = mimeFuer(name, datei.type);
  const storageKey = `${daten.storage.pathPrefix ?? ""}${id}/v1/${slug(name)}`;
  const neu: DocumentView = {
    id,
    folderId,
    name,
    description: null,
    storageKey,
    size: datei.size,
    mimeType,
    checksum: null,
    version: 1,
    uploadedBy: daten.me,
    createdAt: jetzt,
    updatedAt: jetzt,
    versions: [{ version: 1, storageKey, size: datei.size, mimeType, uploadedBy: daten.me, createdAt: jetzt, note: null }],
    shares: [],
    width: null,
    height: null,
    pages: null,
    motiv: null,
    lokaleVorschau: dateiArt(mimeType, name) === "bild" ? URL.createObjectURL(datei) : null,
    adresse: vomServer ? `/api/dokumente/${vomServer}` : null,
  };
  // Ist der Ordner inzwischen weg (geloescht, waehrend es lief), bleibt die Datei draussen.
  aendernSpeicher((b) => (b.folders.some((f) => f.id === folderId) ? { ...b, documents: [...b.documents, neu] } : b));
}

/** Die Endung, die eine neue Version haben muss — oder `null`, wenn sie passt. */
function falscheEndung(d: DocumentView, datei: File): string | null {
  const alt = endung(d.name).toLowerCase();
  const neu = endung(datei.name).toLowerCase();
  return alt && alt !== neu ? alt : null;
}

export function useHochladen({ daten, anfang }: { daten: AblageDaten; anfang: Bestand }) {
  const liste = useSyncExternalStore(
    abonnieren,
    () => posten,
    () => KEINE,
  );
  const router = useRouter();
  const echt = daten.quelle === "datenbank";
  // Viele Dateien auf einmal: EIN Auffrischen, wenn die letzte oben ist.
  const uhrAuffrischen = useRef<ReturnType<typeof setTimeout> | null>(null);
  const auffrischen = useCallback(() => {
    if (uhrAuffrischen.current) clearTimeout(uhrAuffrischen.current);
    uhrAuffrischen.current = setTimeout(() => router.refresh(), 400);
  }, [router]);

  /** Eine Datei hinauf: mit Sitzung wirklich, sonst gespielt. */
  const senden = useCallback(
    (p: Posten, datei: File, ziel: { folderId: string; name: string } | { dokumentId: string }, fertig: (a: Angekommen | null) => void) => {
      if (!echt) {
        starten(p, () => fertig(null));
        return;
      }
      if (datei.size > MAX_BYTES) {
        fehlschlag(p, `Zu groß — höchstens ${MAX_TEXT}.`);
        return;
      }
      const form = new FormData();
      form.set("datei", datei);
      if ("dokumentId" in ziel) form.set("dokumentId", ziel.dokumentId);
      else {
        form.set("folderId", ziel.folderId);
        form.set("name", ziel.name);
      }
      sendenEcht(p, form, (a) => {
        fertig(a);
        auffrischen();
      });
    },
    [echt, auffrischen],
  );

  /** Dateien in einen Ordner. Fragt nach, wo ein Name schon vergeben ist. */
  const hochladen = useCallback(
    async (eingang: File[] | FileList, folderId: string) => {
      const dateien = Array.from(eingang);
      if (dateien.length === 0) return;
      festhalten(anfang);
      const stand = bestandJetzt()!;
      const ziel = pfadText(folderId, stand.folders);

      const bilder = dateien.filter((d) => dateiArt(mimeFuer(d.name, d.type), d.name) === "bild").length;
      if (wirksameSicht(folderId, stand.folders).visibility === "PUBLIC" && bilder > 0) {
        const weiter = await confirmDialog({
          title: bilder === 1 ? "Ein Foto öffentlich ablegen?" : `${formatDecimal(bilder)} Fotos öffentlich ablegen?`,
          description: `Alles in „${ziel}“ ist für jeden sichtbar, auch auf der Website. ${formatDecimal(daten.ohneFotoEinwilligung)} Mitglieder haben Fotos im Internet nicht zugestimmt — bitte nur Bilder ablegen, auf denen sie nicht zu erkennen sind.`,
          confirmLabel: "Trotzdem hochladen",
        });
        if (!weiter) return;
      }

      for (const datei of dateien) {
        const basis = { id: neueId("u"), name: datei.name, groesse: datei.size, folderId, ziel };
        if (datei.size === 0) {
          fehlschlag(basis, "Die Datei ist leer.");
          continue;
        }
        const jetzt = bestandJetzt()!;
        const gleich = jetzt.documents.find(
          (d) => d.folderId === folderId && d.name.toLocaleLowerCase("de") === datei.name.toLocaleLowerCase("de"),
        );
        if (gleich) {
          const offen = laufend().filter((p) => p.dateiId === gleich.id).length;
          const alsVersion = await confirmDialog({
            title: `„${datei.name}“ liegt hier schon`,
            description: `Als neue Version bleibt die bisherige als Version ${gleich.version + offen} erhalten — Adresse und Teilen-Links bleiben gleich. Sonst liegt die neue Datei mit eigenem Namen daneben.`,
            confirmLabel: "Als neue Version",
            cancelLabel: "Beide behalten",
          });
          if (alsVersion) {
            senden({ ...basis, fortschritt: 0, status: "laeuft", dateiId: gleich.id }, datei, { dokumentId: gleich.id }, (a) => {
              neueVersionAblegen(daten, gleich.id, datei, a?.version);
            });
            continue;
          }
        }
        // Vergeben ist, was schon liegt und was gerade in denselben Ordner unterwegs ist.
        const belegt = [
          ...bestandJetzt()!.documents.filter((d) => d.folderId === folderId).map((d) => d.name),
          ...laufend().filter((p) => p.folderId === folderId && !p.dateiId).map((p) => p.name),
        ];
        const name = freierName(datei.name, belegt);
        senden({ ...basis, name, fortschritt: 0, status: "laeuft" }, datei, { folderId, name }, (a) =>
          neuAblegen(daten, datei, a?.name ?? name, folderId, a?.id),
        );
      }
    },
    [anfang, daten, senden],
  );

  const neueVersion = useCallback(
    (dateiId: string, datei: File) => {
      festhalten(anfang);
      const d = bestandJetzt()!.documents.find((x) => x.id === dateiId);
      if (!d) return;
      if (datei.size === 0) {
        toast.error(`${datei.name}: Die Datei ist leer.`);
        return;
      }
      const alt = falscheEndung(d, datei);
      if (alt) {
        toast.error(`Eine neue Version muss wieder eine .${alt}-Datei sein`, {
          description: `„${datei.name}“ lädst du besser als eigene Datei hoch.`,
        });
        return;
      }
      senden(
        {
          id: neueId("u"),
          name: d.name,
          groesse: datei.size,
          folderId: d.folderId ?? "",
          ziel: pfadText(d.folderId, bestandJetzt()!.folders),
          fortschritt: 0,
          status: "laeuft",
          dateiId,
        },
        datei,
        { dokumentId: dateiId },
        (a) => {
          const nummer = neueVersionAblegen(daten, dateiId, datei, a?.version);
          if (nummer) toast.success(`Version ${nummer} von „${d.name}“ ist da`);
        },
      );
    },
    [anfang, daten, senden],
  );

  const aufraeumen = useCallback(() => {
    posten = laufend();
    melden();
  }, []);

  return { posten: liste, hochladen, neueVersion, aufraeumen };
}

/** Die Karte unten rechts: was gerade hochlaedt, was fertig ist, was nicht ging. */
export function Hochladeliste({ posten: liste, onSchliessen }: { posten: Posten[]; onSchliessen: () => void }) {
  if (liste.length === 0) return null;
  const offen = liste.filter((p) => p.status === "laeuft").length;
  const fertig = liste.filter((p) => p.status === "fertig").length;
  const fehler = liste.filter((p) => p.status === "fehler").length;

  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-bg-elevated shadow-e4 fixed right-4 bottom-20 z-40 w-[min(22rem,calc(100vw-2rem))] rounded-xl ring-1 ring-black/[0.06] md:bottom-6 dark:ring-white/[0.08]"
    >
      <div className="border-border-subtle flex items-center justify-between gap-3 border-b py-2 pr-2 pl-4">
        <p className="text-body-sm text-text-primary font-medium tabular-nums">
          {offen > 0
            ? `Lädt hoch · ${formatDecimal(fertig)} von ${formatDecimal(liste.length - fehler)}`
            : `${formatDecimal(fertig)} ${fertig === 1 ? "Datei" : "Dateien"} hochgeladen`}
          {fehler > 0 && <span className="text-danger font-normal"> · {formatDecimal(fehler)} nicht</span>}
        </p>
        <button
          type="button"
          onClick={onSchliessen}
          disabled={offen > 0}
          aria-label="Anzeige schließen"
          title={offen > 0 ? "Erst wenn alles oben ist" : "Schließen"}
          className="text-text-tertiary hover:bg-bg-secondary hover:text-text-primary duration-fast grid h-8 w-8 place-items-center rounded-lg transition-colors disabled:opacity-40"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
      <ul className="max-h-64 space-y-3 overflow-y-auto px-4 py-3">
        {liste.map((p) => (
          <li key={p.id} className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-body-sm text-text-primary min-w-0 flex-1 truncate" title={p.name}>
                {p.name}
              </span>
              {p.status === "fertig" ? (
                <Check className="text-success h-4 w-4 shrink-0" aria-label="fertig" />
              ) : p.status === "fehler" ? (
                <CircleAlert className="text-danger h-4 w-4 shrink-0" aria-label="Fehler" />
              ) : (
                <span className="text-caption text-text-tertiary shrink-0 tabular-nums">{Math.round(p.fortschritt * 100)} %</span>
              )}
            </div>
            {p.status === "fehler" ? (
              <p className="text-caption text-danger">{p.fehler}</p>
            ) : (
              <>
                <div className="bg-bg-tertiary h-1 overflow-hidden rounded-full">
                  <div
                    className={cn("h-full rounded-full transition-[width] duration-150", p.status === "fertig" ? "bg-success" : "bg-accent")}
                    style={{ width: `${Math.round(p.fortschritt * 100)}%` }}
                  />
                </div>
                <p className="text-caption text-text-tertiary truncate tabular-nums">
                  {formatBytes(p.groesse)}
                  {p.dateiId ? " · als neue Version" : ""} · {p.ziel}
                </p>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
